/**
 * Reusable concept autocomplete with infinite scroll (load more on scroll to top/bottom).
 * Used for Product Types in FilterBar; can be reused for other paginated concept dropdowns.
 */
import React, { createContext, useContext, useMemo, useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import type { ConceptValue } from "../../../services/regulationsService";
import { useTheme, alpha } from "@mui/material/styles";

const labelContainerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 0.5,
};

const labelSx = {
  position: "relative" as const,
  transform: "none",
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "text.primary",
};

const countSx = {
  fontSize: "0.75rem",
  color: "text.secondary",
  fontWeight: 400,
};

const LoadingMoreContext = createContext<"next" | "prev" | null>(null);
const ListboxElementContext =
  createContext<React.MutableRefObject<HTMLUListElement | null> | null>(null);
const ListboxMaxHeightContext = createContext(280);

const DROPDOWN_SEARCH_HEADER_HEIGHT = 60;
const DEFAULT_LISTBOX_MAX_HEIGHT = 280;
const MIN_LISTBOX_HEIGHT = 80;

interface DropdownSearchContextValue {
  searchText: string;
  onSearchChange: (text: string) => void;
  placeholder: string;
  searchFocusedRef: React.MutableRefObject<boolean>;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  suppressSearchBlurRef: React.MutableRefObject<boolean>;
  focusSearchInput: () => boolean;
}

const defaultSearchFocusedRef = { current: false };
const defaultSearchInputRef = { current: null };
const defaultSuppressSearchBlurRef = { current: false };

const DropdownSearchContext = createContext<DropdownSearchContextValue>({
  searchText: "",
  onSearchChange: () => {},
  placeholder: "Search...",
  searchFocusedRef: defaultSearchFocusedRef,
  searchInputRef: defaultSearchInputRef,
  suppressSearchBlurRef: defaultSuppressSearchBlurRef,
  focusSearchInput: () => false,
});

const HOVER_OPTION_BG = "#F3F4F6";
const CHIP_BG = "#F3F4F6";
const BORDER_COLOR = "#E5E7EB";
const DIVIDER_COLOR = "#CBD5E1";
const SEARCH_SEPARATOR_COLOR = "#E5E7EB";
const FIELD_VALUE_COLOR = "#111827";

const SearchablePaper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function SearchablePaper({ children, ...rest }, ref) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { searchText, onSearchChange, placeholder, searchFocusedRef, searchInputRef, suppressSearchBlurRef, focusSearchInput } =
    useContext(DropdownSearchContext);

  useLayoutEffect(() => {
    focusSearchInput();
    const raf = requestAnimationFrame(() => focusSearchInput());
    const timer = window.setTimeout(() => focusSearchInput(), 0);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [focusSearchInput]);

  return (
    <Paper
      ref={ref}
      {...rest}
      elevation={0}
      sx={{
        borderRadius: "8px",
        mt: 0.5,
        overflow: "hidden",
        border: `1px solid ${BORDER_COLOR}`,
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          px: "12px",
          pt: "12px",
          pb: "12px",
          borderBottom: `1px solid ${SEARCH_SEPARATOR_COLOR}`,
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <TextField
          size="small"
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          autoFocus
          inputRef={searchInputRef}
          onMouseDown={(e) => e.stopPropagation()}
          onFocus={() => { searchFocusedRef.current = true; }}
          onBlur={() => {
            if (suppressSearchBlurRef.current) return;
            searchFocusedRef.current = false;
          }}
          onKeyDown={(e) => e.stopPropagation()}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              bgcolor: "#FFFFFF",
              "& fieldset": {
                borderColor: BORDER_COLOR,
                borderWidth: "1px",
              },
              "&:hover fieldset": { borderColor: BORDER_COLOR },
              "&.Mui-focused fieldset": {
                borderColor: `${primaryColor} !important`,
                borderWidth: "2px !important",
              },
              "&.Mui-focused": { boxShadow: "none" },
            },
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
              py: "6px",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#9CA3AF",
              opacity: 1,
            },
          }}
        />
      </Box>
      {children}
    </Paper>
  );
});

const ListboxWithLoading = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(function ListboxWithLoading(props, ref) {
  const loadingMore = useContext(LoadingMoreContext);
  const listboxElementRef = useContext(ListboxElementContext);
  const listboxMaxHeight = useContext(ListboxMaxHeightContext);
  const { children, onScroll, ...rest } = props;
  const setListboxRef = useCallback(
    (node: HTMLUListElement | null) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (
          ref as React.MutableRefObject<HTMLUListElement | null>
        ).current = node;
      }
      if (listboxElementRef) {
        listboxElementRef.current = node;
      }
    },
    [ref, listboxElementRef],
  );
  return (
    <ul
      ref={setListboxRef}
      onScroll={onScroll}
      style={{ maxHeight: listboxMaxHeight, overflow: "auto" }}
      {...rest}
    >
      {loadingMore === "prev" && (
        <li
          key="loading-prev"
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 8,
            listStyle: "none",
          }}
        >
          <CircularProgress size={24} />
        </li>
      )}
      {children}
      {loadingMore === "next" && (
        <li
          key="loading-next"
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 8,
            listStyle: "none",
          }}
        >
          <CircularProgress size={24} />
        </li>
      )}
    </ul>
  );
});

export interface InfiniteScrollConceptAutocompleteProps {
  label: string;
  value: ConceptValue[];
  onChange: (event: unknown, value: ConceptValue[]) => void;
  options: ConceptValue[];
  inputValue: string;
  onInputChange: (event: unknown, value: string) => void;
  loading: boolean;
  /** Whether more data can be loaded when scrolling down */
  hasMoreNext?: boolean;
  /** 'next' | 'prev' when loading more at bottom/top */
  loadingMore?: "next" | "prev" | null;
  onLoadMoreNext?: () => void;
  onLoadMorePrev?: () => void;
  maxSelection?: number;
  /** When true, users can select any number of options */
  unlimitedSelection?: boolean;
  /** Hide the selected count label (e.g. "3/5" or "7 selected") */
  hideSelectionCount?: boolean;
  limitTags?: number;
  /**
   * How selected values appear in the closed input.
   * - chips: Chip tags (default)
   * - plainText: comma-separated names (no tag UI)
   */
  selectedDisplayMode?: "chips" | "plainText";
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  /** Use full width (e.g. in dialogs) */
  fullWidth?: boolean;
  /** When searching, hide already-selected options from dropdown list */
  hideSelectedWhenSearching?: boolean;
  /** Selected options visibility behavior while searching */
  selectedVisibilityMode?: "default" | "matchWhenSearchingAllWhenEmpty";
  /** Extra disable rules for options (combined with max-selection limits). */
  getOptionDisabled?: (option: ConceptValue) => boolean;
  /**
   * Normalize selection after each toggle (e.g. All vs specific values exclusivity).
   * Applied to both pending (dropdown open) and committed values.
   */
  normalizeSelection?: (
    next: ConceptValue[],
    previous: ConceptValue[],
  ) => ConceptValue[];
  sx?: FormControlProps["sx"];
}

type FormControlProps = React.ComponentProps<typeof FormControl>;

const SCROLL_THRESHOLD = 50;

function buildOrderedOptions(
  opts: ConceptValue[],
  params: {
    displayValue: ConceptValue[];
    selectedCodes: Set<string>;
    effectiveSearchText: string;
    hideSelectedWhenSearching: boolean;
    isMatchSearchMode: boolean;
  },
): ConceptValue[] {
  const {
    displayValue,
    selectedCodes,
    effectiveSearchText,
    hideSelectedWhenSearching,
    isMatchSearchMode,
  } = params;
  const hasSearchText = effectiveSearchText.length > 0;
  const normalizedSearch = effectiveSearchText.toLowerCase();
  const matchesSearch = (option: ConceptValue) => {
    const name = (option.name ?? "").toLowerCase();
    const code = (option.code ?? "").toLowerCase();
    return (
      normalizedSearch.length === 0 ||
      name.includes(normalizedSearch) ||
      code.includes(normalizedSearch)
    );
  };

  const dedupeByCode = (items: ConceptValue[]) => {
    const seenCodes = new Set<string>();
    const seenNames = new Set<string>();
    return items.filter((item) => {
      const code = item.code.trim();
      const name = (item.name || "").trim().toLowerCase();
      if (code && seenCodes.has(code)) return false;
      if (name && seenNames.has(name)) return false;
      if (code) seenCodes.add(code);
      if (name) seenNames.add(name);
      return true;
    });
  };

  const splitSelectedFirst = (items: ConceptValue[]) => {
    const sel = items.filter((o) => selectedCodes.has(o.code));
    const unsel = items.filter((o) => !selectedCodes.has(o.code));
    return [...sel, ...unsel];
  };

  if (hideSelectedWhenSearching && hasSearchText) {
    if (isMatchSearchMode) {
      const optsCodeSet = new Set(opts.map((o) => o.code));
      const missingMatchedSelected = displayValue.filter(
        (v) => !optsCodeSet.has(v.code) && matchesSearch(v),
      );
      const unique = dedupeByCode([...missingMatchedSelected, ...opts]);
      const sel = unique.filter(
        (o) => selectedCodes.has(o.code) && matchesSearch(o),
      );
      const unsel = unique.filter((o) => !selectedCodes.has(o.code));
      return [...sel, ...unsel];
    }

    return dedupeByCode(
      opts.filter((o) => !selectedCodes.has(o.code)),
    );
  }

  if (!hasSearchText && isMatchSearchMode) {
    const optsCodeSet = new Set(opts.map((o) => o.code));
    const missingSelected = displayValue.filter((v) => !optsCodeSet.has(v.code));
    return splitSelectedFirst(dedupeByCode([...missingSelected, ...opts]));
  }

  const optsCodeSet = new Set(opts.map((o) => o.code));
  const missingSelected = displayValue.filter((v) => !optsCodeSet.has(v.code));
  return splitSelectedFirst(dedupeByCode([...missingSelected, ...opts]));
}

export const InfiniteScrollConceptAutocomplete: React.FC<
  InfiniteScrollConceptAutocompleteProps
> = (props) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const selectedOptionBg = useMemo(
    () => alpha(primaryColor, 0.08),
    [primaryColor],
  );
  const {
    label,
    value,
    onChange,
    options,
    inputValue,
    onInputChange,
    loading,
    hasMoreNext = true,
    loadingMore = null,
    onLoadMoreNext,
    onLoadMorePrev,
    maxSelection = 5,
    unlimitedSelection = false,
    hideSelectionCount = false,
    limitTags = 3,
    selectedDisplayMode = "chips",
    placeholder = "Select...",
    helperText,
    error = false,
    disabled = false,
    fullWidth = false,
    hideSelectedWhenSearching = false,
    selectedVisibilityMode = "default",
    getOptionDisabled: getOptionDisabledProp,
    normalizeSelection,
    sx,
  } = props;

  const [open, setOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState<ConceptValue[]>(value);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [listboxMaxHeight, setListboxMaxHeight] = useState(
    DEFAULT_LISTBOX_MAX_HEIGHT,
  );
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const autocompleteAnchorRef = useRef<HTMLDivElement>(null);
  const listboxElementRef = useRef<HTMLUListElement | null>(null);
  const searchFocusedRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const suppressSearchBlurRef = useRef(false);
  const pendingScrollTarget = useRef<"top" | "firstUnselected" | null>(null);

  const focusSearchInput = useCallback(() => {
    const input = searchInputRef.current;
    if (!input) return false;
    suppressSearchBlurRef.current = true;
    input.focus({ preventScroll: true });
    searchFocusedRef.current = true;
    requestAnimationFrame(() => {
      suppressSearchBlurRef.current = false;
    });
    return true;
  }, []);

  const isFocusInsideDropdown = useCallback(() => {
    const active = document.activeElement;
    if (!active) return false;
    if (searchInputRef.current === active) return true;
    const popper =
      listboxElementRef.current?.closest(".MuiAutocomplete-popper") ??
      searchInputRef.current?.closest(".MuiAutocomplete-popper");
    return Boolean(popper?.contains(active));
  }, []);

  const updateListboxMaxHeight = useCallback(() => {
    const anchor =
      autocompleteAnchorRef.current?.querySelector<HTMLElement>(
        ".MuiInputBase-root",
      ) ?? autocompleteAnchorRef.current;
    if (!anchor) return;
    const { bottom } = anchor.getBoundingClientRect();
    const available =
      window.innerHeight - bottom - DROPDOWN_SEARCH_HEADER_HEIGHT - 12;
    setListboxMaxHeight(
      Math.max(
        MIN_LISTBOX_HEIGHT,
        Math.min(DEFAULT_LISTBOX_MAX_HEIGHT, available),
      ),
    );
  }, []);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setPendingValue(value);
    setDropdownSearch(inputValue ?? "");
    setPopperAnchorEl(autocompleteAnchorRef.current);
    setOpen(true);
    requestAnimationFrame(() => updateListboxMaxHeight());
  }, [disabled, value, inputValue, updateListboxMaxHeight]);

  const commitAndClose = useCallback(() => {
    setOpen(false);
    setPopperAnchorEl(null);
    setDropdownSearch("");
    onInputChange(null, "");
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    wrapperRef.current
      ?.querySelector<HTMLElement>(".MuiInputBase-root")
      ?.blur();
    if (
      pendingValue.length !== value.length ||
      pendingValue.some((v, i) => v.code !== value[i]?.code)
    ) {
      onChange(null as any, pendingValue);
    }
  }, [pendingValue, value, onChange, onInputChange]);

  const handleClose = useCallback(
    (_event: React.SyntheticEvent, reason: string) => {
      if (reason === "blur") {
        requestAnimationFrame(() => {
          if (searchFocusedRef.current) return;
          if (isFocusInsideDropdown()) return;
          if (!wrapperRef.current?.contains(document.activeElement)) {
            commitAndClose();
          }
        });
        return;
      }
      if (reason !== "selectOption" && reason !== "removeOption") {
        commitAndClose();
      }
    },
    [commitAndClose, isFocusInsideDropdown],
  );

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      const popper = listboxElementRef.current?.closest(
        ".MuiAutocomplete-popper",
      );
      if (popper?.contains(target)) return;
      commitAndClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, commitAndClose]);

  useEffect(() => {
    if (!open) return;
    const nextInput = inputValue ?? "";
    if (nextInput !== dropdownSearch) {
      setDropdownSearch(nextInput);
    }
  }, [open, inputValue, dropdownSearch]);

  useLayoutEffect(() => {
    if (!open) {
      setListboxMaxHeight(DEFAULT_LISTBOX_MAX_HEIGHT);
      return;
    }
    updateListboxMaxHeight();
    const handleReposition = () => updateListboxMaxHeight();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, updateListboxMaxHeight]);

  useLayoutEffect(() => {
    if (!open) return;
    focusSearchInput();
    const raf = requestAnimationFrame(() => {
      focusSearchInput();
    });
    const retryTimers = [0, 50, 100, 150].map((delay) =>
      window.setTimeout(() => focusSearchInput(), delay),
    );
    return () => {
      cancelAnimationFrame(raf);
      retryTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [open, focusSearchInput]);

  useLayoutEffect(() => {
    if (!open || loading) return;
    focusSearchInput();
  }, [open, loading, focusSearchInput]);

  useEffect(() => {
    if (open && pendingScrollTarget.current && !loading) {
      const timer = setTimeout(() => {
        const listbox = listboxElementRef.current;
        if (listbox) {
          if (pendingScrollTarget.current === "top") {
            listbox.scrollTop = 0;
          } else if (pendingScrollTarget.current === "firstUnselected") {
            const firstUnselected = listbox.querySelector(
              'li[data-unselected="true"]',
            );
            if (firstUnselected instanceof HTMLElement) {
              listbox.scrollTop = firstUnselected.offsetTop;
            }
          }
          // Always reset after one positioning attempt once options have loaded
          pendingScrollTarget.current = null;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, dropdownSearch, options, loading]);

  const displayValue = open ? pendingValue : value;

  const selectedCodes = useMemo(
    () => new Set(displayValue.map((v) => v.code)),
    [displayValue],
  );

  const effectiveSearchText = (inputValue ?? dropdownSearch).trim();
  const isMatchSearchMode =
    selectedVisibilityMode === "matchWhenSearchingAllWhenEmpty";

  const orderedOptions = useMemo(
    () =>
      buildOrderedOptions(options, {
        displayValue,
        selectedCodes,
        effectiveSearchText,
        hideSelectedWhenSearching,
        isMatchSearchMode,
      }),
    [
      options,
      displayValue,
      selectedCodes,
      effectiveSearchText,
      hideSelectedWhenSearching,
      isMatchSearchMode,
    ],
  );

  const lastSelectedCode = useMemo(() => {
    const selectedInList = orderedOptions.filter((o) =>
      selectedCodes.has(o.code),
    );
    return selectedInList.length > 0
      ? selectedInList[selectedInList.length - 1].code
      : null;
  }, [orderedOptions, selectedCodes]);

  const hasUnselectedInList = useMemo(
    () => orderedOptions.some((o) => !selectedCodes.has(o.code)),
    [orderedOptions, selectedCodes],
  );

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
    const nearTop = el.scrollTop < SCROLL_THRESHOLD;
    if (nearBottom && hasMoreNext && !loadingMore && onLoadMoreNext) {
      onLoadMoreNext();
    }
    if (nearTop && !loadingMore && onLoadMorePrev) {
      onLoadMorePrev();
    }
  };

  const displayHelperText =
    helperText ??
    (unlimitedSelection
      ? `${displayValue.length} selected`
      : `${displayValue.length}/${maxSelection}`);
  const isSelectionLimited = !unlimitedSelection;
  const isSingleSelect = isSelectionLimited && maxSelection === 1;

  return (
    <DropdownSearchContext.Provider
      value={{
        searchText: dropdownSearch,
        onSearchChange: (text) => {
          setDropdownSearch(text);
          const hasText = text.trim().length > 0;
          if (hasText) {
            pendingScrollTarget.current = isMatchSearchMode
              ? "top"
              : "firstUnselected";
          } else if (isMatchSearchMode) {
            pendingScrollTarget.current = "top";
          } else {
            pendingScrollTarget.current = null;
          }
          onInputChange(null, text);
        },
        placeholder: "Search...",
        searchFocusedRef,
        searchInputRef,
        suppressSearchBlurRef,
        focusSearchInput,
      }}
    >
      <LoadingMoreContext.Provider value={loadingMore}>
        <ListboxMaxHeightContext.Provider value={listboxMaxHeight}>
        <ListboxElementContext.Provider value={listboxElementRef}>
          <FormControl
            ref={wrapperRef}
            fullWidth={fullWidth}
            error={error}
            sx={
              fullWidth
                ? { maxWidth: "none", ...sx }
                : { minWidth: 120, flex: 1, ...sx }
            }
          >
            {label || !hideSelectionCount ? (
            <Box sx={labelContainerSx}>
            {label ? (
              <InputLabel shrink sx={labelSx}>
                {label}
              </InputLabel>
            ) : null}
            {!hideSelectionCount ? (
            <Tooltip
              title={
                isSelectionLimited ? `Select up to ${maxSelection}` : "Select values"
              }
              placement="top"
              arrow
            >
              <Typography component="span" sx={countSx}>
                {displayHelperText}
              </Typography>
            </Tooltip>
            ) : null}
          </Box>
            ) : null}
            <Box ref={autocompleteAnchorRef}>
            <Autocomplete<ConceptValue, true>
            multiple
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            options={options}
            value={displayValue}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name || ""
            }
            isOptionEqualToValue={(option, val) => option.code === val.code}
            inputValue=""
            onInputChange={() => {}}
            onChange={(e, newValue, reason) => {
              if (isSingleSelect) {
                let next: ConceptValue[] = [];
                if (reason === "clear" || reason === "removeOption") {
                  next = newValue ?? [];
                } else if (reason === "selectOption") {
                  const prevCodes = new Set(
                    displayValue.map((item) => item.code),
                  );
                  const added = (newValue ?? []).filter(
                    (item) => !prevCodes.has(item.code),
                  );
                  next =
                    added.length > 0
                      ? [added[added.length - 1]]
                      : (newValue ?? []).slice(-1);
                } else {
                  next = (newValue ?? []).slice(0, 1);
                }

                setPendingValue(next);

                if (reason === "selectOption" || reason === "clear") {
                  setOpen(false);
                  setPopperAnchorEl(null);
                  setDropdownSearch("");
                  onInputChange(null, "");
                  onChange(e, next);
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                  wrapperRef.current
                    ?.querySelector<HTMLElement>(".MuiInputBase-root")
                    ?.blur();
                } else if (!open) {
                  onChange(e, next);
                }
                return;
              }

              const clamped =
                !isSelectionLimited || (newValue?.length ?? 0) <= maxSelection
                  ? newValue ?? []
                  : displayValue;
              const normalized = normalizeSelection
                ? normalizeSelection(clamped, displayValue)
                : clamped;
              if (open) {
                setPendingValue(normalized);
              } else {
                onChange(e, normalized);
              }
            }}
            limitTags={-1}
            autoHighlight={false}
            // disableCloseOnSelect
            forcePopupIcon
            getOptionDisabled={(option) => {
              if (getOptionDisabledProp?.(option)) return true;
              const isSelected = selectedCodes.has(option.code);
              if (isSelected || !isSelectionLimited) return false;
              // Single-select: allow choosing another option to replace
              if (isSingleSelect) return false;
              return displayValue.length >= maxSelection;
            }}
            loading={loading}
            disabled={disabled}
            PaperComponent={SearchablePaper}
            filterOptions={(opts) =>
              buildOrderedOptions(opts, {
                displayValue,
                selectedCodes,
                effectiveSearchText,
                hideSelectedWhenSearching,
                isMatchSearchMode,
              })
            }
            renderOption={(optionProps, option) => {
              const { key: _key, ...restProps } = optionProps as any;
              const isSelected = selectedCodes.has(option.code);
              const showDivider =
                option.code === lastSelectedCode && hasUnselectedInList;
              return (
                <React.Fragment key={option.code}>
                  <li
                    {...restProps}
                    data-unselected={!isSelected ? "true" : undefined}
                    data-divider={showDivider ? "true" : undefined}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 16px",
                      minHeight: "unset",
                      boxSizing: "border-box",
                      ...(isSelected
                        ? { backgroundColor: selectedOptionBg }
                        : {}),
                    }}
                  >
                    <Tooltip
                      title={typeof option === "string" ? option : option.name}
                      placement="top"
                      enterDelay={400}
                      enterNextDelay={200}
                    >
                      <Typography
                        noWrap
                        component="span"
                        sx={{
                          flex: 1,
                          fontSize: "0.875rem",
                          minWidth: 0,
                          color: "text.primary",
                        }}
                      >
                        {typeof option === "string" ? option : option.name}
                      </Typography>
                    </Tooltip>
                    {isSelected && (
                      <CheckIcon
                        sx={{
                          fontSize: 18,
                          color: primaryColor,
                          ml: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </li>
                </React.Fragment>
              );
            }}
            ListboxComponent={ListboxWithLoading}
            slotProps={{
              popper: {
                anchorEl: popperAnchorEl,
                placement: "bottom-start",
                // MUI Autocomplete popper slot typing omits strategy in some versions
                ...({ strategy: "fixed" } as object),
                sx: { zIndex: (t) => t.zIndex.modal + 1 },
                container:
                  typeof document !== "undefined" ? document.body : undefined,
                modifiers: [
                  {
                    name: "offset",
                    options: { offset: [0, 4] },
                  },
                  {
                    // Keep dropdown below the field; flipping upward covers label + input in dialogs.
                    name: "flip",
                    enabled: false,
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      mainAxis: false,
                      altAxis: true,
                      tether: false,
                      rootBoundary: "viewport",
                      padding: 8,
                    },
                  },
                ],
              },
              listbox: {
                onScroll: handleScroll,
                onFocus: () => {
                  focusSearchInput();
                },
                sx: {
                  maxHeight: listboxMaxHeight,
                  py: 0.5,
                  "& .MuiAutocomplete-option": {
                    py: 0,
                    px: 0,
                    minHeight: "unset",
                  },
                  "& .MuiAutocomplete-option[data-divider='true']": {
                    borderBottom: `1px solid ${DIVIDER_COLOR} !important`,
                    marginBottom: "4px !important",
                    borderRadius: 0,
                  },
                  "& .MuiAutocomplete-option[aria-selected='true']": {
                    backgroundColor: `${selectedOptionBg} !important`,
                  },
                  "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
                    backgroundColor: `${selectedOptionBg} !important`,
                  },
                  "& .MuiAutocomplete-option[aria-selected='true']:hover": {
                    backgroundColor: `${selectedOptionBg} !important`,
                  },
                  "& .MuiAutocomplete-option[aria-selected='false'].Mui-focused": {
                    backgroundColor: HOVER_OPTION_BG,
                  },
                  "& .MuiAutocomplete-option[aria-selected='false']:hover": {
                    backgroundColor: HOVER_OPTION_BG,
                  },
                },
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                flexWrap: "nowrap",
                overflow: "hidden",
                paddingRight: "36px !important",
                height: 40,
                boxSizing: "border-box",
                borderRadius: "6px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${BORDER_COLOR} !important`,
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: `${BORDER_COLOR} !important`,
              },
              // Beat dialog-scoped focus overrides; empty fields stay neutral grey.
              "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${BORDER_COLOR} !important`,
                borderWidth: "1px !important",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${BORDER_COLOR} !important`,
                borderWidth: "1px !important",
              },
              "& .MuiAutocomplete-endAdornment": {
                right: "4px !important",
              },
              "& .MuiChip-root .MuiChip-label": {
                color: `${FIELD_VALUE_COLOR} !important`,
                fontWeight: 500,
              },
              "& .MuiAutocomplete-tag .MuiChip-label": {
                color: `${FIELD_VALUE_COLOR} !important`,
                WebkitTextFillColor: FIELD_VALUE_COLOR,
              },
            }}
            renderTags={(tagValue, getTagProps) => {
              // Single-select or plain-text mode: show names, not chip/tag UI
              if (isSingleSelect || selectedDisplayMode === "plainText") {
                if (tagValue.length === 0) return null;
                const seenNames = new Set<string>();
                const names = tagValue
                  .map((item) => (item.name || item.code || "").trim())
                  .filter((name) => {
                    const key = name.toLowerCase();
                    if (!key || seenNames.has(key)) return false;
                    seenNames.add(key);
                    return true;
                  });
                const label = isSingleSelect
                  ? names[0] ?? ""
                  : names.join(", ");
                if (!label) return null;
                return (
                  <Typography
                    noWrap
                    component="span"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: FIELD_VALUE_COLOR,
                      pl: 0.5,
                      maxWidth: "calc(100% - 28px)",
                      lineHeight: "40px",
                    }}
                  >
                    {label}
                  </Typography>
                );
              }

              return (
              <Box
                ref={(el: HTMLDivElement | null) => {
                  if (!el) return;
                  const handler = (ev: WheelEvent) => {
                    if (ev.deltaY !== 0 && el.scrollWidth > el.clientWidth) {
                      el.scrollLeft += ev.deltaY;
                      ev.preventDefault();
                      ev.stopPropagation();
                    }
                  };
                  el.onwheel = handler;
                }}
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  overflowY: "hidden",
                  flexWrap: "nowrap",
                  maxWidth: "calc(100% - 20px)",
                  alignItems: "center",
                  gap: "4px",
                  "&::-webkit-scrollbar": { height: 3 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.15)",
                    borderRadius: 2,
                  },
                }}
              >
                {tagValue.slice(0, limitTags).map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index }) as any;
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      label={option.name}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.75rem",
                        flexShrink: 0,
                        bgcolor: CHIP_BG,
                        color: FIELD_VALUE_COLOR,
                        border: "none",
                        "& .MuiChip-label": {
                          px: 1,
                          color: `${FIELD_VALUE_COLOR} !important`,
                          fontWeight: 500,
                          WebkitTextFillColor: FIELD_VALUE_COLOR,
                        },
                        "& .MuiChip-deleteIcon": {
                          fontSize: 14,
                          color: "#9CA3AF",
                          backgroundColor: "transparent",
                          "&:hover": {
                            color: "text.secondary",
                            backgroundColor: "transparent",
                          },
                        },
                      }}
                    />
                  );
                })}
                {tagValue.length > limitTags && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    +{tagValue.length - limitTags}
                  </Typography>
                )}
              </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={error}
                placeholder={displayValue.length === 0 ? placeholder : ""}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    readOnly: true,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                  htmlInput: {
                    ...params.inputProps,
                    readOnly: true,
                    tabIndex: -1,
                    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
                      event.target.blur();
                      if (open) {
                        focusSearchInput();
                      }
                    },
                  },
                }}
              />
            )}
            />
            </Box>
            {error && helperText ? (
              <FormHelperText>{helperText}</FormHelperText>
            ) : null}
          </FormControl>
        </ListboxElementContext.Provider>
        </ListboxMaxHeightContext.Provider>
      </LoadingMoreContext.Provider>
    </DropdownSearchContext.Provider>
  );
};
